import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Skeleton, SpecraBadge, SpecraButton, SpecraHeading, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { rfiSectionClient } from './rfiSectionHooks';
function sourceHref(issue) {
    if (issue.sourceKind === 'question') return `#rfi-question-${issue.sourceId}`;
    if (issue.sourceKind === 'section') return `#rfi-section-${issue.sourceId}`;
    if (issue.sourceKind === 'follow_up') return `#rfi-follow-up-${issue.sourceId}`;
    if (issue.sourceKind === 'package_item') return `#rfi-package-item-${issue.sourceId}`;
    if (issue.sourceKind === 'package') return `#rfi-package-${issue.sourceId}`;
    if (issue.sourceId === 'questions') return '#rfi-questions';
    if (issue.sourceId === 'follow-ups') return '#rfi-follow-ups';
    return '#rfi-packages';
}
export function RfiReadinessPanel({ rfiResponseProjectId }) {
    const cache = useQueryClient();
    const query = useQuery({
        queryKey: [
            'rfi-readiness',
            rfiResponseProjectId
        ],
        queryFn: ({ signal })=>rfiSectionClient.getRfiReadiness({
                rfiResponseProjectId
            }, {
                signal
            }),
        enabled: Boolean(rfiResponseProjectId),
        retry: false,
        // Also refresh after collaborative Office callback saves and evidence changes.
        refetchInterval: 15000
    });
    useEffect(()=>{
        const refresh = ()=>{
            void cache.invalidateQueries({
                queryKey: [
                    'rfi-readiness',
                    rfiResponseProjectId
                ]
            });
        };
        const unsubscribe = cache.getQueryCache().subscribe((event)=>{
            const key = event.query.queryKey;
            if (event.type === 'updated' && event.action.type === 'success' && typeof key[0] === 'string' && [
                'rfi-questions',
                'rfi-sections',
                'rfi-follow-ups',
                'rfi-packages',
                'rfi-source-versions',
                'rfi-evidence-history'
            ].includes(key[0]) && key.some((value)=>typeof value === 'object' && value !== null ? 'rfiResponseProjectId' in value && value.rfiResponseProjectId === rfiResponseProjectId : value === rfiResponseProjectId)) refresh();
        });
        window.addEventListener('rfi-document-saved', refresh);
        return ()=>{
            unsubscribe();
            window.removeEventListener('rfi-document-saved', refresh);
        };
    }, [
        cache,
        rfiResponseProjectId
    ]);
    const data = query.data;
    return /*#__PURE__*/ _jsxs("section", {
        className: "flex max-w-3xl flex-col items-start gap-[var(--space-3)]",
        "data-testid": Testids.rfiReadiness.panel,
        children: [
            /*#__PURE__*/ _jsx(SpecraHeading, {
                level: 2,
                children: "Advisory readiness"
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                children: "Readiness supports the team's decision and does not guarantee government acceptance. Outstanding issues do not prevent recording an actual submission. Only saved content is checked."
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "outline",
                onClick: ()=>void query.refetch(),
                disabled: query.isFetching,
                "data-testid": Testids.rfiReadiness.refresh,
                children: "Refresh readiness"
            }),
            query.isFetching && /*#__PURE__*/ _jsxs("output", {
                "data-testid": Testids.rfiReadiness.loading,
                className: "block",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        children: "Checking saved questions, content, proof and packages…"
                    }),
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-24"
                    })
                ]
            }),
            query.isError ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                role: "alert",
                "data-testid": Testids.rfiReadiness.error,
                children: "Readiness could not be checked. No checks have passed. Refresh readiness to retry, or review the source items directly. You can still record an actual submission."
            }) : data && !query.isFetching ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "p",
                        "data-testid": Testids.rfiReadiness.summary,
                        children: [
                            data.questions.length,
                            " source items · ",
                            data.sectionCount,
                            " sections ·",
                            ' ',
                            data.draftedSections,
                            " drafted · ",
                            data.reviewedSections,
                            " reviewed · ",
                            data.openFollowUps,
                            ' ',
                            "open follow-ups · ",
                            data.packageCount,
                            " proposed packages · ",
                            data.packageItemCount,
                            " final items"
                        ]
                    }),
                    data.questions.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        children: "No source items yet. Add questions or extract a source to assess coverage."
                    }),
                    data.questions.map((q)=>/*#__PURE__*/ _jsxs("article", {
                            className: "flex w-full flex-col items-start gap-[var(--space-2)] rounded-md border border-border p-[var(--space-3)]",
                            "data-testid": Testids.rfiReadiness.question,
                            children: [
                                /*#__PURE__*/ _jsx("a", {
                                    href: `#rfi-question-${q.questionId}`,
                                    children: q.label || 'Question source unavailable'
                                }),
                                /*#__PURE__*/ _jsxs(SpecraText, {
                                    as: "p",
                                    children: [
                                        "Mapped: ",
                                        q.mapped ? 'yes' : 'no',
                                        " · Drafted: ",
                                        q.drafted ? 'yes' : 'no',
                                        " · Reviewed:",
                                        ' ',
                                        q.reviewed ? 'yes' : 'no',
                                        " · Ready: ",
                                        q.ready ? 'yes' : 'no'
                                    ]
                                })
                            ]
                        }, q.questionId)),
                    /*#__PURE__*/ _jsxs(SpecraHeading, {
                        level: 3,
                        children: [
                            "Individual issues (",
                            data.issues.length,
                            ")"
                        ]
                    }),
                    data.issues.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        children: "No issues identified by the supported checks. Review the exact package and agency instructions manually; acceptance is not guaranteed."
                    }),
                    data.issues.map((issue, index)=>/*#__PURE__*/ _jsxs("article", {
                            className: "flex w-full flex-col items-start gap-[var(--space-2)] rounded-md border border-border p-[var(--space-3)]",
                            "data-testid": Testids.rfiReadiness.issue,
                            children: [
                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: "warning",
                                    children: issue.state === 'not_checked' ? 'Not checked / unsupported' : 'Incomplete'
                                }),
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    children: issue.explanation
                                }),
                                /*#__PURE__*/ _jsxs(SpecraText, {
                                    as: "p",
                                    children: [
                                        "Source:",
                                        ' ',
                                        /*#__PURE__*/ _jsx("a", {
                                            "data-testid": Testids.rfiReadiness.source,
                                            href: sourceHref(issue),
                                            children: issue.sourceLabel || issue.sourceId
                                        })
                                    ]
                                })
                            ]
                        }, `${issue.code}-${issue.sourceId}-${index}`))
                ]
            }) : null
        ]
    });
}
